import React, { useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Transition } from 'react-transition-group';
import { Portal } from "../Portal";
import { usePositioner } from "../../hooks/usePositioner";
import { reflow } from "../../utils/reflow";
import { easeCurves } from "../../motion/eases";
var PositionContainer = styled.div.withConfig({
  displayName: "Positioner__PositionContainer",
  componentId: "vapor__sc-1xr3ucd-0"
})(["position:", ";z-index:", ";opacity:1;transition:opacity 0.15s ", ";&[aria-hidden='true']{pointer-events:none;}&[data-popper-reference-hidden]{opacity:0;pointer-events:none;}"], function (p) {
  return p.isUsingPositioner ? 'absolute' : 'relative';
}, function (p) {
  return p.zIndex;
}, easeCurves.primary);
export var PopoverStatusContext = React.createContext({
  isShowing: false
});
/**
 * Positioner
 *
 * The primary popover component which handles mounting / unmounting
 * animations, positioning, and rendering of the popover content.
 * This is typically used as part of Popover, instead of directly,
 * but may be used independently for specific use-cases, namely
 * "dumb" popovers that you want to position manually.
 */

var DEFAULT_OFFSET = [0, 0];
export var Positioner = React.forwardRef(function Positioner(_ref, ref) {
  var _ref$unmountOnExit = _ref.unmountOnExit,
      unmountOnExit = _ref$unmountOnExit === void 0 ? true : _ref$unmountOnExit,
      _ref$mountOnEnter = _ref.mountOnEnter,
      mountOnEnter = _ref$mountOnEnter === void 0 ? true : _ref$mountOnEnter,
      _ref$shouldUsePortal = _ref.shouldUsePortal,
      shouldUsePortal = _ref$shouldUsePortal === void 0 ? false : _ref$shouldUsePortal,
      boundary = _ref.boundary,
      isShowing = _ref.isShowing,
      transitionDuration = _ref.transitionDuration,
      children = _ref.children,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? DEFAULT_OFFSET : _ref$offset,
      _ref$shouldUsePositio = _ref.shouldUsePositioner,
      shouldUsePositioner = _ref$shouldUsePositio === void 0 ? false : _ref$shouldUsePositio,
      target = _ref.target,
      zIndex = _ref.zIndex,
      autoHide = _ref.autoHide;
  var popoverRef = useRef(null);
  var hasTarget = !!target;

  var _usePositioner = usePositioner(),
      popperJSRef = _usePositioner.popperJSRef,
      positionPopover = _usePositioner.positionPopover,
      popperProvidedPlacement = _usePositioner.placement;
  /**
   * Position the popover when the transition
   * first starts.
   */


  var handleTransitionEnter = useCallback(function (node) {
    reflow(node);

    if (shouldUsePositioner) {
      positionPopover({
        targetEl: target,
        popoverRef: popoverRef,
        placement: placement,
        autoHide: autoHide,
        offset: offset,
        boundary: boundary,
        enableGpuAcceleration: true
      });
    }
  }, [shouldUsePositioner, positionPopover, target, placement, autoHide, offset, boundary]);
  var handleTransitionEntering = useCallback(function () {
    // This is used primarily to ensure that arrows
    // are visible and poisitioned correctly if
    // present.
    window.requestAnimationFrame(function () {
      if (popperJSRef.current) {
        popperJSRef.current.update();
      }
    });
  }, [popperJSRef]);
  var isShowingStatusContext = useMemo(function () {
    return {
      isShowing: isShowing
    };
  }, [isShowing]);
  var showPopover = shouldUsePositioner ? isShowing && hasTarget : isShowing;
  var renderedPlacement = shouldUsePositioner ? popperProvidedPlacement : placement;
  return /*#__PURE__*/React.createElement(Transition, {
    in: showPopover,
    onEnter: handleTransitionEnter,
    onEntering: handleTransitionEntering,
    mountOnEnter: mountOnEnter,
    unmountOnExit: unmountOnExit,
    timeout: transitionDuration
  }, function (state) {
    return /*#__PURE__*/React.createElement(Portal, {
      disabled: !shouldUsePortal
    }, /*#__PURE__*/React.createElement(PopoverStatusContext.Provider, {
      value: isShowingStatusContext
    }, /*#__PURE__*/React.createElement(PositionContainer, {
      isUsingPositioner: shouldUsePositioner,
      ref: popoverRef,
      "aria-hidden": !showPopover,
      zIndex: zIndex
    }, children({
      ref: ref,
      state: state,
      position: renderedPlacement
    }))));
  });
});
export var testExports = {
  PositionContainer: PositionContainer
};